import { z } from "zod";

// Shared API response schemas. Single source of truth for the shape of
// every HTTP response the web app consumes. Use z.infer<> to derive the
// TypeScript type, and .safeParse() to validate at the boundary.
//
// This module is the new home for types that used to live inline in
// apps/web/src/api.ts. Migration is incremental — add endpoints here as
// you touch them; once every endpoint has a schema, drop the duplicated
// interfaces in the web app.

// -- Membership (used by login + /auth/me) -------------------------------
export const membershipSchema = z.object({
  organisation_id: z.string(),
  slug: z.string(),
  name: z.string(),
  role: z.enum([
    "super_admin",
    "tenant_admin",
    "moderator",
    "teacher",
    "registrar",
    "student",
  ]),
  is_examiner: z.boolean(),
});
export type Membership = z.infer<typeof membershipSchema>;

// -- Login ---------------------------------------------------------------
// Mirrors apps/api/src/routes/auth.ts POST /v1/auth/login response.
// Refresh tokens are NOT present here — they ride on the HttpOnly
// `eb_refresh` cookie set by the API. The access token is short-lived
// (15 min) and held only in the SPA's in-memory store.
export const loginResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    full_name: z.string(),
    // Founder-grace verify state. `needs_verification` is true whenever the
    // account's email isn't yet verified; a founder-in-grace still logs in but
    // sees the nag banner until `verification_grace_until` (or they verify).
    needs_verification: z.boolean(),
    verification_grace_until: z.string().nullable(),
  }),
  memberships: z.array(membershipSchema),
  active_organisation_id: z.string().nullable(),
  access_token: z.string(),
  access_expires_in: z.number(),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

// POST /v1/auth/register returns a DIFFERENT, flat shape from login — the org
// is brand-new (status 'provisioning') with exactly one founder membership, so
// the server returns the founder's ids + the access token directly rather than
// the login envelope. Mirrors apps/api/src/routes/auth.ts. (Register must NOT be
// parsed with loginResponseSchema — the shapes differ, which silently broke
// signup: the Zod parse threw and the UI showed a generic connectivity error.)
export const registerResponseSchema = z.object({
  user_id: z.string(),
  organisation_id: z.string(),
  organisation_slug: z.string(),
  organisation_status: z.string(),
  access_token: z.string(),
  access_expires_in: z.number(),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

// -- /auth/me -----------------------------------------------------------
// Mirrors GET /v1/auth/me. status is wider than membership.status on the
// login response because `me` surfaces the verbatim row (including the
// `invited` state) for the account-switcher UI.
//
// `impersonation` is non-null whenever the caller's active session is a
// super_admin impersonating the tenant; the web top-bar banner reads it
// to show the shadowed-state indicator.
export const impersonationStateSchema = z.object({
  session_id: z.string(),
  target_organisation_id: z.string(),
  target_slug: z.string(),
  started_at: z.string(),
  reason: z.string().nullable(),
  // Structured category + whether the session is capped at read-only and
  // when it will auto-expire. All three came in with the hardening pass
  // (migration 0014); legacy sessions read null / default-true / null.
  reason_category: z.string().nullable().optional(),
  view_as_readonly: z.boolean().optional(),
  expires_at: z.string().nullable().optional(),
  view_as_role: z
    .enum(["tenant_admin", "moderator", "teacher", "registrar", "student"])
    .nullable()
    .optional(),
});
export type ImpersonationState = z.infer<typeof impersonationStateSchema>;

// -- Profile (GET /v1/org/me/profile) -----------------------------------
export const profileResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    full_name: z.string(),
    locale: z.string(),
    email_verified_at: z.string().nullable(),
    created_at: z.string(),
    last_login_at: z.string().nullable(),
    avatar_r2_key: z.string().nullable().optional(),
  }),
});
export type ProfileResponse = z.infer<typeof profileResponseSchema>;

export const meResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    full_name: z.string(),
    // Founder-grace verify state — drives the SPA's persistent verify banner.
    needs_verification: z.boolean(),
    verification_grace_until: z.string().nullable(),
  }),
  memberships: z.array(
    membershipSchema.extend({ status: z.string() }),
  ),
  active_organisation_id: z.string().nullable(),
  impersonation: impersonationStateSchema.nullable().optional(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;
